import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabaseClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatBRL } from "@/lib/simulator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, Plus, FileText, Crown, LogOut, Car, Download, Shield, LayoutDashboard, History, Search, ArrowUpRight, Lock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { downloadPdf } from "@/lib/downloadPdf";
import { motion, AnimatePresence } from "framer-motion";

interface Simulation {
  id: string;
  marca: string;
  modelo: string;
  ano: number;
  km: number;
  valor_fipe: number;
  valor_estimado: number;
  score_condicao: number | null;
  nivel_dano: string | null;
  created_at: string;
}

interface Profile {
  plan: string | null;
  payment_status: string | null;
  full_name: string;
}

export default function UserHome() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [loadingReport, setLoadingReport] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      toast.success("Pagamento confirmado! Seu plano Pro está ativo.");
    } else if (paymentStatus === "failure") {
      toast.error("Pagamento não concluído. Tente novamente.");
    }
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      const currentUser = data.session?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        const { data: profileData } = await supabase
          .from("profile")
          .select("plan, payment_status, full_name")
          .eq("user_id", currentUser.id)
          .single();
        setProfile(profileData);

        const { data: sims, error: simsError } = await supabase
          .from("simulacoes")
          .select("id, marca, modelo, ano, km, valor_fipe, valor_estimado, score_condicao, nivel_dano, created_at")
          .eq("user_id", currentUser.id)
          .order("created_at", { ascending: false });
        
        if (simsError) console.error("[DASHBOARD] Error fetching simulations:", simsError);
        console.log("[DASHBOARD] Simulations found for user:", sims?.length || 0);
        setSimulations(sims || []);
      }
    })();
  }, []);

  async function handleLogout() {
    await supabase.auth.signOut();
    window.location.replace("/login");
  }

  async function handleViewReport(simId: string) {
    setLoadingReport(simId);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        toast.error("Faça login para ver o relatório");
        return;
      }

      const res = await fetch(
        "https://nbhzbstabtkjrhrwfhoj.supabase.co/functions/v1/generate-report",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ simulacao_id: simId }),
        }
      );
      const data = await res.json();
      if (data.html) {
        const w = window.open("", "_blank");
        if (w) {
          w.document.write(data.html);
          w.document.close();
        }
      } else {
        toast.error("Erro ao gerar relatório");
      }
    } catch {
      toast.error("Erro ao gerar relatório");
    } finally {
      setLoadingReport(null);
    }
  }

  const planLabel = profile?.plan === "pro" ? "Pro" : profile?.plan === "enterprise" ? "Enterprise" : "Starter";
  const isPro = profile?.plan === "pro" || profile?.plan === "enterprise";

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Brutalist Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none opacity-50"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-accent/5 pointer-events-none"></div>

      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="max-w-5xl mx-auto space-y-12">
          
          {/* Dashboard Header / Profile */}
          <header className="flex flex-col md:flex-row items-center justify-between gap-8 pb-8 border-b-4 border-foreground">
            <div className="space-y-2 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3">
                <LayoutDashboard className="w-8 h-8 text-primary" />
                <h1 className="text-4xl lg:text-5xl font-heading font-black uppercase tracking-tighter">Terminal::DASH</h1>
              </div>
              <p className="text-muted-foreground font-mono text-sm tracking-widest uppercase">
                Usuário: <span className="text-foreground">{user?.email?.split('@')[0]}</span> | Plano: <span className="text-primary font-bold">{planLabel}</span>
              </p>
            </div>

            <div className="flex items-center gap-4">
              <Button onClick={handleLogout} variant="outline" className="border-2 border-foreground shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all font-bold uppercase">
                <LogOut className="w-4 h-4 mr-2" /> Encerrar Sessão
              </Button>
            </div>
          </header>

          {/* Top Actions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/simulador" className="group">
              <div className="bg-primary text-primary-foreground p-8 border-4 border-foreground shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] group-hover:translate-x-1 group-hover:translate-y-1 group-hover:shadow-none transition-all h-full flex flex-col justify-between overflow-hidden relative">
                <div className="absolute -right-4 -top-4 opacity-10 group-hover:scale-110 transition-transform duration-500">
                  <Plus className="w-32 h-32" />
                </div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-heading font-black uppercase tracking-tighter">Nova Perícia</h3>
                  <p className="mt-2 text-primary-foreground/80 font-medium">Inicie uma análise avançada com IA agora.</p>
                </div>
                <div className="mt-8 flex items-center font-bold uppercase text-xs tracking-widest">
                  Iniciar Protocolo <ArrowUpRight className="ml-2 w-4 h-4" />
                </div>
              </div>
            </Link>

            {!isPro && (
              <Link to="/precos" className="group">
                <div className="bg-accent text-accent-foreground p-8 border-4 border-foreground shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] group-hover:translate-x-1 group-hover:translate-y-1 group-hover:shadow-none transition-all h-full flex flex-col justify-between overflow-hidden relative">
                  <div className="absolute -right-4 -top-4 opacity-10 group-hover:scale-110 transition-transform duration-500">
                    <Crown className="w-32 h-32" />
                  </div>
                  <div className="relative z-10">
                    <h3 className="text-2xl font-heading font-black uppercase tracking-tighter text-primary">Upgrade PRO</h3>
                    <p className="mt-2 text-accent-foreground/80 font-medium">Desbloqueie mapas de calor e PDFs profissionais.</p>
                  </div>
                  <div className="mt-8 flex items-center font-bold uppercase text-xs tracking-widest text-primary">
                    Ver Planos <ArrowUpRight className="ml-2 w-4 h-4" />
                  </div>
                </div>
              </Link>
            )}

            <div className="bg-muted/30 p-8 border-4 border-foreground shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] flex flex-col justify-between">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <History className="w-6 h-6 text-muted-foreground" />
                  <Badge variant="outline" className="font-mono border-muted-foreground/30">{simulations.length} REGISTROS</Badge>
                </div>
                <h3 className="text-2xl font-heading font-black uppercase tracking-tighter">Histórico</h3>
                <p className="text-muted-foreground text-sm font-medium leading-tight">Acesso rápido aos seus laudos gerados anteriormente.</p>
              </div>
            </div>
          </div>

          {/* Simulations Section */}
          <div className="space-y-8">
            {(!isPro && simulations.length > 0) ? (
              <div className="relative overflow-hidden border-4 border-primary bg-primary/5 p-12 text-center space-y-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <Lock className="w-24 h-24" />
                </div>
                <div className="space-y-4 relative z-10">
                  <div className="inline-flex items-center justify-center p-4 bg-primary text-primary-foreground rounded-full mb-4">
                    <Shield className="w-12 h-12" />
                  </div>
                  <h2 className="text-3xl font-heading font-black uppercase tracking-tighter">Acesso Restrito ao Histórico</h2>
                  <p className="text-muted-foreground font-medium max-w-2xl mx-auto text-lg leading-relaxed">
                    Detectamos <span className="text-primary font-bold">{simulations.length} simulações</span> salvas em sua conta. 
                    Para acessar o histórico completo, baixar PDFs e ver detalhes técnicos, você precisa de uma assinatura <span className="text-primary font-bold">PRO</span>.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-4">
                  <Button asChild size="xl" className="font-black uppercase tracking-widest bg-primary text-primary-foreground hover:scale-105 transition-transform w-full sm:w-auto">
                    <Link to="/precos">
                      <Crown className="mr-2 w-6 h-6" /> Desbloquear Agora
                    </Link>
                  </Button>
                  <Button variant="outline" size="xl" asChild className="border-2 border-foreground font-black uppercase tracking-widest w-full sm:w-auto">
                    <Link to="/simulador">
                      <Plus className="mr-2 w-6 h-6" /> Nova Perícia
                    </Link>
                  </Button>
                </div>
                <div className="flex items-center justify-center gap-8 pt-8 border-t border-primary/20 flex-wrap">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-primary" /> Relatórios PDF
                  </div>
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-primary" /> Histórico Ilimitado
                  </div>
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-primary" /> Backup Seguro
                  </div>
                </div>
              </div>
            ) : simulations.length === 0 ? (
              <div className="border-4 border-dashed border-muted-foreground/20 p-20 text-center bg-muted/5">
                <div className="relative inline-block">
                  <Search className="w-16 h-16 text-muted-foreground/30 mx-auto" />
                  <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 4, ease: "linear" }}
                    className="absolute inset-0 border-2 border-dashed border-primary/20 rounded-full"
                  />
                </div>
                <p className="text-muted-foreground mt-6 font-mono uppercase tracking-widest text-sm">Nenhum rastro de análise encontrado no sistema.</p>
                <Button asChild className="mt-8 bg-foreground hover:bg-foreground/90 text-background font-black uppercase tracking-tighter">
                  <Link to="/simulador">Gerar Primeiro Laudo</Link>
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-20">
                <AnimatePresence>
                  {simulations.map((sim, idx) => (
                    <motion.div
                      key={sim.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <Card className="group relative bg-background p-0 border-4 border-foreground shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all overflow-hidden rounded-none">
                        {/* Card Header Pattern */}
                        <div className="h-2 bg-primary"></div>
                        <div className="p-6 space-y-6">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">COD::{sim.id.slice(0, 8)}</span>
                              <h3 className="text-2xl font-heading font-black uppercase tracking-tighter leading-none">
                                {sim.marca} {sim.modelo}
                              </h3>
                              <p className="text-muted-foreground font-mono text-xs font-bold uppercase tracking-widest">
                                ANO {sim.ano} • {sim.km?.toLocaleString("pt-BR")} KM
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              {sim.score_condicao && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="bg-foreground text-background px-3 py-1 font-heading font-black text-lg skew-x-[-12deg] cursor-help">
                                      {sim.score_condicao}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Score de Condição (0-100)
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {sim.nivel_dano && (
                                <div className="flex flex-col items-end gap-1">
                                  <Badge variant="secondary" className="font-black uppercase text-[10px] tracking-widest rounded-none">
                                    DANO::{sim.nivel_dano}
                                  </Badge>
                                  <Badge variant="outline" className="border-primary/30 text-[8px] font-black uppercase tracking-widest rounded-none py-0 px-1 opacity-70">
                                    <Shield className="w-2 h-2 mr-1" /> Prot. 70/30
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 border-y-2 border-foreground/5 py-4">
                            <div className="space-y-0.5">
                              <p className="text-[10px] font-bold text-muted-foreground uppercase opacity-60">FIPE Referência</p>
                              <p className="font-heading font-black text-xl tracking-tighter">{formatBRL(sim.valor_fipe)}</p>
                            </div>
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1">
                                <p className="text-[10px] font-bold text-primary uppercase">Valor Real Estimado</p>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="w-2.5 h-2.5 text-primary/60 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Preço justo de mercado para este estado técnico.
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                              <p className="font-heading font-black text-xl tracking-tighter text-primary">{formatBRL(sim.valor_estimado)}</p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-mono text-muted-foreground uppercase">
                              DATA::{new Date(sim.created_at).toLocaleDateString("pt-BR")}
                            </span>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewReport(sim.id)}
                                disabled={loadingReport === sim.id}
                                className="border-2 border-foreground rounded-none font-bold uppercase text-[10px] h-8"
                              >
                                <FileText className="mr-1 w-3 h-3" />
                                {loadingReport === sim.id ? "Analys..." : "Dossiê"}
                              </Button>
                              {isPro && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => downloadPdf(sim.id)}
                                  className="border-2 border-foreground bg-foreground text-background hover:bg-foreground/90 rounded-none font-bold uppercase text-[10px] h-8"
                                >
                                  <Download className="mr-1 w-3 h-3" />
                                  PDF
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
